import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import {FormBuilder, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import {NgbActiveModal, NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {ToastrService} from 'ngx-toastr';
import {AppValidators, fromControl, TranslatePipe} from '@brunata-angular/util';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {CostsTableComponent} from '../costs-table/costs-table.component';
import {
    CostSpecificationControl,
    GroupControl,
    updateFormGroupControls,
} from '../reporting-form-controls';
import {WebbillCostSpecifications} from '../models/webbill-cost-specification.model';
import {OnlineCostSpecifications} from '@reporting/costs/models/specification.model';
import {combineLatest} from 'rxjs';

@Component({
    selector: 'app-add-new-cost-group',
    templateUrl: './add-new-cost-group-modal.component.html',
    styleUrls: ['./add-new-cost-group-modal.component.scss'],
    standalone: true,
    imports: [
        TranslatePipe,
        FormsModule,
        ReactiveFormsModule,
        NgbModule,
        CostsTableComponent,
        InputTextGroupComponent,
    ],
    providers: [ToastrService],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddNewCostGroupModalComponent implements OnInit {
    @Input() costUnitMap = new Map<string, string>([]);
    @Input() vatMap = new Map<string | null, string>([]);
    @Input() lineTextMap = new Map<string, string>([]);
    @Input() nextGroupNo: number | undefined;
    @Output() submitData = new EventEmitter<GroupControl>();
    formGroup: GroupControl = this.formBuilder.group({
        costSpecifications: this.formBuilder.array<CostSpecificationControl>(
            [],
            Validators.minLength(1),
        ),
        costGroupId: this.formBuilder.control<string | null>(null),
        webBillCostGroupId: this.formBuilder.control<string | null>(null),
        rowVersion: this.formBuilder.control(''),
        groupName: this.formBuilder.control(''),
        groupNo: this.formBuilder.control<number | null>(null),
        enabled: this.formBuilder.control(true, {nonNullable: true}),
    });

    constructor(
        public activeModal: NgbActiveModal,
        private formBuilder: FormBuilder,
        private changeDetectorRef: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        if (this.nextGroupNo !== undefined) {
            this.formGroup.controls.groupNo.setValue(this.nextGroupNo);
        }
        this.addCostSpecification(this.formGroup);
    }

    addCostSpecification(groupControl: GroupControl): void {
        if (this.nextGroupNo !== undefined) {
            groupControl.controls.groupNo.setValue(this.nextGroupNo);
        }
        const formGroup: CostSpecificationControl = this.createCostSpecificationControl();
        groupControl.controls.costSpecifications.push(formGroup);
        this.changeDetectorRef.detectChanges();
    }

    private createCostSpecificationControl(): CostSpecificationControl {
        const formGroup = this.formBuilder.group({
            amount: this.formBuilder.control<number | null>(null, [
                Validators.required,
                AppValidators.preciseNumber(13, 2),
            ]),
            lineAmountDeviationWarning: this.formBuilder.control<boolean>(false, {
                nonNullable: true,
            }),
            vat: this.formBuilder.control<string>(''),
            consumptionCost: this.formBuilder.control<boolean>(false, {nonNullable: true}),
            description: this.formBuilder.control<string>('', {
                nonNullable: true,
                validators: [Validators.required, Validators.maxLength(60)],
            }),
            consumptionAmount: this.formBuilder.control<number | null>(
                {value: null, disabled: true},
                [AppValidators.preciseNumber(13, 3)],
            ),
            consumptionAmountDeviationWarning: this.formBuilder.control<boolean>(false, {
                nonNullable: true,
            }),
            consumptionUnit: this.formBuilder.control<string | null>(
                {value: null, disabled: true},
                [Validators.maxLength(50)],
            ),
            calculatedByBrunata: this.formBuilder.control<boolean>(false, {nonNullable: true}),

            deleted: this.formBuilder.control<boolean>(false, {nonNullable: true}),
            webBillCostSpecification: this.formBuilder.control<WebbillCostSpecifications | null>(
                null,
            ),
            onlineCostSpecification: this.formBuilder.control<OnlineCostSpecifications | null>(
                null,
            ),
        });

        combineLatest({
            consumptionCost: fromControl(formGroup.controls.consumptionCost),
            calculatedByBrunata: fromControl(formGroup.controls.calculatedByBrunata),
            deleted: fromControl(formGroup.controls.deleted),
        }).subscribe(({consumptionCost, calculatedByBrunata, deleted}) => {
            updateFormGroupControls(formGroup, {
                consumptionCost,
                calculatedByBrunata,
                deleted,
            });
        });

        return formGroup;
    }

    submit(): void {
        this.formGroup.markAllAsTouched();
        this.changeDetectorRef.detectChanges();
        if (this.formGroup.valid) {
            this.submitData.emit(this.formGroup);
            this.activeModal.close();
        }
    }
}
